// @flow

import get from 'lodash/get';
import { toASCII } from 'punycode.js';
import { createReducer } from 'redux-act';

import type { SearchState } from '@gandi/redux-organization';

import type { State } from '../types';

import {
  tldInfo,
  saveSearch,
  toggleSpinner,
  setWorkingFlag,
  unsetWorkingFlag,
  startAddingToCart,
  stopAddingToCart,
  startRemovingFromCart,
  stopRemovingFromCart,
  requestTransferInAvailability,
  receiveTransferInAvailability,
  receiveTransferInPrices,
  setFallbackAvailability,
  resetAllResults,
  resetSearch,
} from './actions';

const EMPTY_OBJECT = {};

const initialState: State = {
  search: {
    spinner: false,
    input: '',
  },
  results: EMPTY_OBJECT,
};

const requestTransferInAvailabilityHandler = (state: State, fqdn: string) => {
  const asciiFqdn = toASCII(fqdn);

  if (state.results[asciiFqdn]) {
    return state;
  }

  return {
    ...state,
    results: {
      ...state.results,
      [asciiFqdn]: {
        ...state.results[asciiFqdn],
        fqdn: asciiFqdn,
        isTransferable: false,
        availabilityMsg: '',
        corporate: false,
        internal: false,
        invalid: false,
        availability: 'unknown',
      },
    },
  };
};

const receiveTransferInAvailabilityHandler = (
  state: State,
  {
    data,
    fqdn,
  }: {
    data: any,
    fqdn: string,
  },
) => {
  const asciiFqdn = toASCII(fqdn);

  if (
    state.results[asciiFqdn] &&
    state.results[asciiFqdn].isTransferable === !!data.available
  ) {
    return state;
  }

  return {
    ...state,
    results: {
      ...state.results,
      [asciiFqdn]: {
        ...state.results[asciiFqdn],
        fqdn: asciiFqdn,
        isTransferable: !!data.available,
        availabilityMsg: data.msg || get(data, 'errors.0.description', ''),
        durations: data.durations || [1],
        corporate: !!data.corporate,
        internal: !!data.internal,
        invalid: !!data.invalid,
        availability: 'unknown',
      },
    },
  };
};

const setFallbackAvailabilityHandler = (
  state: State,
  {
    availability,
    fqdn,
  }: {
    availability: 'available' | 'unavailable' | 'unknown',
    fqdn: string,
  },
): State => {
  const asciiFqdn = toASCII(fqdn);

  if (
    state.results[asciiFqdn] &&
    state.results[asciiFqdn].availability === availability
  ) {
    return state;
  }

  return {
    ...state,
    results: {
      ...state.results,
      [asciiFqdn]: {
        ...state.results[asciiFqdn],
        availability,
      },
    },
  };
};

const tldInfoSuccessHandler: (
  state: State,
  {
    response: any,
    fqdn: string,
  },
) => State | SearchState = (state, { response, fqdn }) => {
  const asciiFqdn = toASCII(fqdn);

  if (
    state.results[asciiFqdn] &&
    state.results[asciiFqdn].tld === !!response.data.full_tld
  ) {
    return state;
  }

  return {
    ...state,
    results: {
      ...state.results,
      [asciiFqdn]: {
        ...state.results[asciiFqdn],
        extTrade: !!response.data.ext_trade,
        authinfoForTransfer: get(response.data, 'authinfo_for_transfer', true),
        tld: response.data.full_tld || '',
        corporate:
          !!response.data.corporate || !!state.results[asciiFqdn].corporate,
      },
    },
  };
};

const transferInPricesSuccessHandler: (
  state: State,
  {
    data: any,
    fqdn: string,
  },
) => State | SearchState = (state, { data: { result }, fqdn }) => {
  const asciiFqdn = toASCII(fqdn);
  if (state.results[asciiFqdn] && state.results[asciiFqdn].price) {
    return state;
  }

  return {
    ...state,
    results: {
      ...state.results,
      [asciiFqdn]: {
        ...state.results[asciiFqdn],
        price: {
          currency: result.prices.currency,
          amount: {
            afterTaxes: result.prices.products[0].prices[0].price_after_taxes,
            beforeTaxes: result.prices.products[0].prices[0].price_before_taxes,
          },
          duration: result.prices.products[0].prices[0].min_duration,
          change: result.prices.products[0].prices[0].options.duration_change,
        },
      },
    },
  };
};

const setWorkingFlagHandler = (state: State, fqdn: string) => {
  const asciiFqdn = toASCII(fqdn);
  return {
    ...state,
    results: {
      ...state.results,
      [asciiFqdn]: {
        ...state.results[asciiFqdn],
        fqdn: asciiFqdn,
        isWorking: true,
      },
    },
  };
};

const unsetWorkingFlagHandler = (state: State, fqdn: string) => {
  const asciiFqdn = toASCII(fqdn);

  return {
    ...state,
    results: {
      ...state.results,
      [asciiFqdn]: {
        ...state.results[asciiFqdn],
        fqdn: asciiFqdn,
        isWorking: false,
      },
    },
  };
};

const startAddingToCartHandler = (state: State, fqdn: string) => {
  const asciiFqdn = toASCII(fqdn);

  return {
    ...state,
    results: {
      ...state.results,
      [asciiFqdn]: {
        ...state.results[asciiFqdn],
        isWorking: true,
        isBeingAddedToCart: true,
      },
    },
  };
};

const stopAddingToCartHandler = (state: State, fqdn: string) => {
  const asciiFqdn = toASCII(fqdn);

  return {
    ...state,
    results: {
      ...state.results,
      [asciiFqdn]: {
        ...state.results[asciiFqdn],
        isWorking: false,
        isBeingAddedToCart: false,
      },
    },
  };
};

const startRemovingFromCartHandler = (state: State, fqdn: string) => ({
  ...state,
  results: {
    ...state.results,
    [fqdn]: {
      ...state.results[fqdn],
      isWorking: true,
      isBeingRemovedFromCart: true,
    },
  },
});

const stopRemovingFromCartHandler = (state: State, fqdn: string) => ({
  ...state,
  results: {
    ...state.results,
    [fqdn]: {
      ...state.results[fqdn],
      isWorking: false,
      isBeingRemovedFromCart: false,
    },
  },
});

/**
 * Show/hide the spinner.
 */
const toggleSpinnerHandler = (state: State, bool: boolean) => ({
  ...state,
  search: {
    ...state.search,
    spinner: bool,
  },
});

/**
 * Reset all results (when starting a new search).
 */
const resetAllResultsHandler = (state: State) => ({
  ...state,
  results: EMPTY_OBJECT,
});

/**
 * Reset search input value.
 */
const resetSearchHandler = (state: State) => ({
  ...state,
  search: { input: '' },
});

/**
 * Save a new search.
 */
const saveSearchHandler = (state: State, data: string | Array<string>) => ({
  ...state,
  search: {
    ...initialState.search,
    input: ((Array.isArray(data) ? data.join(' ') : data) || '')
      .toLowerCase()
      .replace(/\s/g, ' ')
      .trim()
      .split(',')
      .join(' '),
  },
});

export default (createReducer(
  {
    // $FlowFixMe[invalid-computed-prop]
    [saveSearch]: saveSearchHandler,
    // $FlowFixMe[invalid-computed-prop]
    [toggleSpinner]: toggleSpinnerHandler,

    // $FlowFixMe[invalid-computed-prop]
    [setWorkingFlag]: setWorkingFlagHandler,
    // $FlowFixMe[invalid-computed-prop]
    [unsetWorkingFlag]: unsetWorkingFlagHandler,

    // $FlowFixMe[invalid-computed-prop]
    [startAddingToCart]: startAddingToCartHandler,
    // $FlowFixMe[invalid-computed-prop]
    [stopAddingToCart]: stopAddingToCartHandler,

    // $FlowFixMe[invalid-computed-prop]
    [startRemovingFromCart]: startRemovingFromCartHandler,
    // $FlowFixMe[invalid-computed-prop]
    [stopRemovingFromCart]: stopRemovingFromCartHandler,

    // $FlowFixMe[invalid-computed-prop]
    [requestTransferInAvailability]: requestTransferInAvailabilityHandler,
    // $FlowFixMe[invalid-computed-prop]
    [receiveTransferInAvailability]: receiveTransferInAvailabilityHandler,

    // $FlowFixMe[invalid-computed-prop]
    [setFallbackAvailability]: setFallbackAvailabilityHandler,

    [tldInfo.fetchOne.success.getType()]: tldInfoSuccessHandler,

    // $FlowFixMe[invalid-computed-prop]
    [receiveTransferInPrices]: transferInPricesSuccessHandler,
    // $FlowFixMe[invalid-computed-prop]
    [resetAllResults]: resetAllResultsHandler,
    // $FlowFixMe[invalid-computed-prop]
    [resetSearch]: resetSearchHandler,
  },
  initialState,
): {
  (): (state: State, payload?: mixed, meta?: mixed) => State,
  assignAll: ({ ... } | Array<mixed>, { ... } | Array<mixed>) => mixed,
  batch: ({ ... } | Array<mixed>) => mixed,
  bindAll: ({ ... } | Array<mixed>, { ... } | Array<mixed>) => mixed,
  disbatch: (mixed, void | Array<mixed>) => mixed,
  has: (actionCreator: mixed) => boolean,
  // $FlowFixMe[cannot-resolve-name]
  off: Reducer$Off,
  // $FlowFixMe[cannot-resolve-name]
  on: Reducer$On<State>,
  options: () => mixed,
  ...
});
